import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  CircularProgress,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ListResponseOfCertificateTemplatesOverrideExtended,
  ResponseOfCertificateTemplate,
} from "services/KnowfullyAdminClient";
import {
  Orientation,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import translateError from "utils/translateError";

const styles = {
  paper: {
    border: "4px solid #F0E4D3",
    borderRadius: "5px",
    margin: "20px",
  },
};

const CertificateTemplateDetails = () => {
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const {
    certificateService,
    certificateTemplatesUrl,
    certificateTemplateEditUrl,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const certificateTemplateResult = useQuery<ResponseOfCertificateTemplate>(
    ["certificateTemplate", id],
    () => certificateService.getCertificateTemplate(id!),
    {
      enabled: !!id,
    }
  );

  const certificateTemplate = certificateTemplateResult.data?.data;
  useTitle(certificateTemplate?.name || "Certificate Template Details");

  const {
    data: certificateTemplatesOverrideData,
    isLoading: isLoadingCertificateTemplatesOverrides,
  } = useQuery<ListResponseOfCertificateTemplatesOverrideExtended>(
    ["certificateTemplatesOverrides", id],
    () => certificateService.getCertificateTemplatesOverrides(id!, null),
    { enabled: !!id }
  );

  if (!id) return <Navigate to={certificateTemplatesUrl()} replace />;
  if (certificateTemplateResult.isLoading) return <Loader />;
  if (certificateTemplateResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="certificateTemplateLoadError" />
        </Alert>
      </Box>
    );
  }

  const handleDelete = async () => {
    try {
      await registerPromise(certificateService.deleteCertificateTemplate(id));
      queryClient.invalidateQueries("certificateTemplates");
      deleteOpenActions.setFalse();
      navigate(certificateTemplatesUrl());
    } catch (error: any) {
      setSnackbarMessage(
        new SnackbarMessageProps(translateError(error, t), Severity.Error)
      );
    }
  };

  const rows = [
    {
      title: t({ id: "id" }),
      component: certificateTemplate?.id && (
        <span>{certificateTemplate.id}</span>
      ),
    },
    {
      title: t({ id: "organization" }),
      component: certificateTemplate?.organizationName && (
        <span>{certificateTemplate.organizationName}</span>
      ),
    },
    {
      title: t({ id: "certificateTemplateFormNameLabel" }),
      component: certificateTemplate?.name && (
        <span>{certificateTemplate.name}</span>
      ),
    },
    {
      title: t({ id: "certificateTemplateFormValueLabel" }),
      component: certificateTemplate?.value && (
        <span>{certificateTemplate.value}</span>
      ),
    },
    {
      title: t({ id: "certificateTemplateFormDescriptionLabel" }),
      component: certificateTemplate?.description && (
        <span>{certificateTemplate.description}</span>
      ),
    },
    {
      title: t({ id: "certificateTemplateFormUnaccreditedLabel" }),
      component: certificateTemplate?.unaccredited && (
        <span>{certificateTemplate.unaccredited}</span>
      ),
    },
    {
      title: t({ id: "orientation" }),
      component: certificateTemplate && (
        <span>
          {certificateTemplate?.isLandscape
            ? Orientation.Landscape
            : Orientation.Portrait}
        </span>
      ),
    },
    {
      title: t({ id: "certificateTemplateFormContentLabel" }),
      component: certificateTemplate?.templateHtml && (
        <Paper sx={styles.paper}>
          <ResponsiveIframe
            srcDoc={certificateTemplate.templateHtml}
            title="templateHtml"
          />
        </Paper>
      ),
    },
    {
      title: t({ id: "certificateTemplateProfessionTemplateOverridenLabel" }),
      component:
        !isLoadingCertificateTemplatesOverrides &&
        certificateTemplatesOverrideData !== undefined ? (
          <ul>
            {certificateTemplatesOverrideData?.data?.map((p) => (
              <li key={p.id}>
                {p.profession ?? t({ id: "certificateCompletionLabel" })}
              </li>
            ))}
          </ul>
        ) : (
          <CircularProgress size={24} />
        ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={certificateTemplatesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h2>{certificateTemplate?.name}</h2>

      <Box my={3} display="flex" justifyContent="center">
        <Link
          to={certificateTemplateEditUrl(id)}
          style={{ textDecoration: "none" }}
        >
          <Fab color="primary" data-cy="editButton">
            <EditIcon />
          </Fab>
        </Link>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  scope="row"
                  style={{
                    fontWeight: "bold",
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </TableCell>
                <TableCell width="100%">{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="certificateTemplateDeleteTitle"
        contentKey="certificateTemplateDeleteInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default CertificateTemplateDetails;
